/**
 * Writing a voice's loudness into the orb engine's `level`.
 *
 * A frame arrives ten times a second and carries four 25 ms blocks
 * (`voice-level.ts`). Writing only the frame's level would give the orb ten
 * lumps a second, and a syllable is shorter than a lump; so each frame's
 * blocks are written one after another, each at its own moment, and the orb
 * reads forty steps a second, a frame's length behind the voice — which the
 * orb's own attack envelope is longer than anyway.
 *
 * **Plain writes on a timer, not a Reanimated animation.** The first version
 * ran each frame as a `withSequence` of linear timings with reduced motion
 * switched off, and that spent three things *Motion* keeps to one file each: a
 * raw duration, a hand-spelled easing, and the spinner's `ReduceMotion.Never`
 * (`motion.enforcement.test.ts`). None of it was buying anything: the orb
 * already smooths what it is handed (`stepOrb`'s attack and release, and the
 * shells the swell lags through), so a step written at the right moment is
 * exactly as smooth on screen as one tweened into place. And this is data,
 * not decoration — reduced motion is decided where the orb draws it
 * (`stepOrb`'s `still`), never here.
 *
 * Apart from `voice-level.ts` because it writes a shared value, which a test
 * cannot load, and the arithmetic can be tested.
 */
import type { SharedValue } from 'react-native-reanimated';

/** One step of a level sequence: the value to reach, and how long it holds. */
export type LevelStep = {
  readonly value: number;
  readonly ms: number;
};

/** The steps still waiting for their moment, per level, so a new frame or a hush can drop them. */
const waiting = new WeakMap<SharedValue<number>, ReturnType<typeof setTimeout>[]>();

const drop = (level: SharedValue<number>): void => {
  const timers = waiting.get(level);
  if (timers === undefined) return;
  for (const timer of timers) clearTimeout(timer);
  waiting.delete(level);
};

/** Write a frame's blocks into `level`, one after another, replacing whatever was still to come. */
export const writeLevelSteps = (level: SharedValue<number>, steps: readonly LevelStep[]): void => {
  if (steps.length === 0) return;
  drop(level);
  const [first, ...rest] = steps;
  if (first === undefined) return;
  level.value = first.value;
  const timers: ReturnType<typeof setTimeout>[] = [];
  let at = first.ms;
  for (const step of rest) {
    const value = step.value;
    timers.push(
      setTimeout(() => {
        level.value = value;
      }, Math.max(0, Math.round(at))),
    );
    at += step.ms;
  }
  if (timers.length > 0) waiting.set(level, timers);
};

/**
 * Bring `level` to rest: nobody is speaking, or nobody is being heard. A
 * write of zero is enough — the orb's release envelope is the fall a person
 * sees, so the orb settles rather than drops.
 */
export const hushLevel = (level: SharedValue<number>): void => {
  drop(level);
  level.value = 0;
};
